'use client';

import * as React from 'react';
import Image from 'next/image';
import { useLocale, useTranslations } from 'next-intl';
import { Button } from '@/components/ui/button';
import { SectionWrapper } from '@/components/ui/section-wrapper';
import { Typography } from '@/components/ui/typography';
import { useScrollAnimation } from '@/hooks/use-scroll-animation';
import { SectionHeading } from '@/components/frontend/home/section-heading';

export function AIMLMTools() {
    const locale = useLocale();
    const t = useTranslations('home.aimlm');
    const sectionRef = useScrollAnimation({ threshold: 0.15 });

    const content = {
        pillLabel: t('pillLabel'),
        title: t('title'),
        description: t('description'),
        additionalDescription: t('additionalDescription'),
        viewAllButton: t('viewAllButton'),
        downloadSectionTitle: t('downloadSectionTitle'),
        downloadButton: t('downloadButton'),
        downloadUrl: '/images/common/AI-Features-of-Business-MLM-Software.pdf',
        imageSrc: '/images/home/AI-Driven-img.webp',
        imageAlt: t('imageAlt'),
    };

    return (
        <SectionWrapper
            ref={sectionRef.ref}
            variant="gradient"
            padding="md"
            className="relative"
        >
            {/* Background accents */}
            <div className="pointer-events-none absolute inset-0 opacity-40">
                <div className="absolute left-1/4 top-0 h-96 w-96 rounded-full bg-primary-500/5 blur-3xl" />
                <div className="absolute right-1/4 bottom-0 h-96 w-96 rounded-full bg-blue-500/5 blur-3xl" />
            </div>

            <div className="relative z-10">
                <div className="grid lg:grid-cols-[60%_40%] items-center">
                    {/* Content Section */}
                    <div
                        className={`pr-0 lg:pr-12 transition-all duration-700 ${sectionRef.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                            }`}
                    >
                        <SectionHeading
                            pill={content.pillLabel}
                            title={content.title}
                            subtitle=""
                            isVisible={sectionRef.isVisible}
                            align="left"
                            maxWidthClass="max-w-4xl"
                            className="!mb-4"
                        />
                        <Typography variant="p" className="mb-4">
                            {content.description}
                        </Typography>
                        <Typography variant="p" className="mb-4">
                            {content.additionalDescription}
                        </Typography>

                        {/* Download Section */}
                        <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-800">
                            <Typography variant="h3" className="mb-6">
                                {content.downloadSectionTitle}
                            </Typography>
                            <Button
                                href={content.downloadUrl}
                                variant="primary"
                                size="md"
                                showArrow={true}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {content.downloadButton}
                            </Button>
                        </div>
                    </div>

                    {/* Image Section */}
                    <div
                        className={`transition-all duration-700 ${sectionRef.isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 '
                            }`}
                        style={{ transitionDelay: '200ms' }}
                    >
                        <div className="bg-black border border-gray-900 dark:border-gray-900 rounded-2xl p-4 md:pl-[20px] mt-20 lg:mt-0">
                            <Image
                                src={content.imageSrc}
                                alt="AI-Powered MLM Software Tools - Intelligent Network Marketing Automation Platform with Machine Learning Features"
                                width={600}
                                height={600}
                                className="w-full h-auto object-contain"
                                loading="lazy"
                                quality={70}
                                sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 600px"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </SectionWrapper>
    );
}

